import type { CapitalGlobalAssistanceCatalogItem } from '@/features/quotes/schemas/capital-global/coverages-catalog';
import { Switch, Text } from '@noorden-insurtech/alba-design-system';
import type { ReactNode } from 'react';
import { useRef } from 'react';
import {
  getAppShellMain,
  restoreAppShellMainScrollTop,
} from '@/shared/utils/app-shell-main-scroll.util';

function readSwitchChecked(details: unknown): boolean | undefined {
  if (typeof details === 'boolean') return details;
  if (
    details &&
    typeof details === 'object' &&
    'checked' in details &&
    typeof (details as { checked: unknown }).checked === 'boolean'
  ) {
    return (details as { checked: boolean }).checked;
  }
  return undefined;
}

export type AssistanceCatalog = CapitalGlobalAssistanceCatalogItem;

export type AssistanceState = {
  enabled: boolean;
};

/* eslint-disable no-unused-vars -- callback parameter names document arity for callers */
type Props = {
  assistances: AssistanceCatalog[];
  states: Record<string, AssistanceState>;
  onToggle: (code: string, enabled: boolean) => void;
  disabled?: boolean;
  /** Erro por assistência (code → mensagem). Usado pelo conflito de exclusão
   *  mútua, que pode acusar um toggle quando ele colide com uma cobertura. */
  errors?: Record<string, string | undefined>;
  /** Extra content in the same card (e.g. funeral assistance select — Figma 6054:58673). */
  bottomSlot?: ReactNode;
};
/* eslint-enable no-unused-vars */

/** Capital Global assistance rows — toggle only, no capital / %. */
export function AssistanceToggles({
  assistances,
  states,
  onToggle,
  disabled,
  errors,
  bottomSlot,
}: Props) {
  const mainScrollBeforeToggleRef = useRef(0);

  return (
    <div className='flex flex-col rounded-xl border border-alba-gray-300 bg-white divide-y divide-alba-gray-300'>
      {assistances.map(entry => {
        const state = states[entry.code];
        if (!state) return null;
        return (
          <div
            key={entry.code}
            className='flex flex-row items-center justify-between gap-4 px-4 py-3'
            onPointerDownCapture={() => {
              const m = getAppShellMain();
              mainScrollBeforeToggleRef.current = m?.scrollTop ?? 0;
            }}
          >
            <div className='flex flex-col gap-1 flex-1 min-w-0'>
              <Text className='text-sm font-bold text-alba-black-500'>
                {entry.title}
              </Text>
              {entry.description && (
                <Text className='text-xs text-alba-gray-600'>
                  {entry.description}
                </Text>
              )}
              {errors?.[entry.code] && (
                <Text className='text-xs text-alba-error-red-500'>
                  {errors[entry.code]}
                </Text>
              )}
            </div>

            <Switch.Root
              checked={state.enabled}
              onCheckedChange={details => {
                const checked = readSwitchChecked(details);
                if (typeof checked !== 'boolean') return;
                const y = mainScrollBeforeToggleRef.current;
                onToggle(entry.code, checked);
                restoreAppShellMainScrollTop(y);
              }}
              disabled={disabled}
            >
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
              <Switch.Label className='sr-only'>{entry.title}</Switch.Label>
              <Switch.HiddenInput />
            </Switch.Root>
          </div>
        );
      })}
      {bottomSlot ? (
        <div className='px-4 py-3 max-w-[605px]'>{bottomSlot}</div>
      ) : null}
    </div>
  );
}
